import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useDebounce, useToast } from "../../../../shared/hooks";

import { PoliceToolbar } from "../PolicesToolbar";
import { TablePolices } from "../TablePolices";
import { PoliceMenu } from "../Policesmenu";

import type { PoliceTab } from "../TablePolices";
import type { PoliceEvent } from "../../types/Polices";

import { FireServices } from "../../../../shared/services/api/fire/FireServices";
import { policeMenuActions } from "../policeMenuActions";
import { ModalPreviewDocument } from "../../../../shared/components/ModalPreviewDocument/ModalPreviewDocument";
import ModalFirePoliceDetails from "../../modals/ModalFirePoliceDetails";
import { ModalFirePoliceCancel } from "../../modals/ModalFirePoliceCancel";

// -------------------------------------------------------------
// Intervalo padrão do mês
// -------------------------------------------------------------
function getMonthDefaultRange() {
	const today = new Date();
	const ini = new Date(today.getFullYear(), today.getMonth(), 1)
		.toISOString()
		.split("T")[0];

	const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0)
		.toISOString()
		.split("T")[0];

	return { ini, fim };
}

// -------------------------------------------------------------
export const TabPolices = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const navigate = useNavigate();

	// =========================
	// Filtros
	// =========================
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState(getMonthDefaultRange().ini);
	const [dataFim, setDataFim] = useState(getMonthDefaultRange().fim);
	const [status, setStatus] = useState("todos");
	const [clientId, setClientId] = useState<string | null>(null);

	const [appliedFilters, setAppliedFilters] = useState({
		text: "",
		dataIni,
		dataFim,
		status: "todos",
		clientId: null as string | null,
	});

	// =========================
	// Tabela
	// =========================
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [data, setData] = useState<PoliceEvent[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	// =========================
	// Modals / Menu
	// =========================
	const [modalState, setModalState] = useState({
		open: false,
		row: undefined as PoliceEvent | undefined,
		initialTab: "dados" as PoliceTab,
	});

	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedMenuRow, setSelectedMenuRow] = useState<PoliceEvent | null>(null);

	// 🆕 PDF Modal State
	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	const [pdfUrl, setPdfUrl] = useState<string | null>(null);

	// 🆕 Cancel Modal State
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [cancelRow, setCancelRow] = useState<PoliceEvent | null>(null);

	// ---------------------------------------------------------
	// Fetch principal
	// ---------------------------------------------------------
	useEffect(() => {
		setIsLoading(true);

		const doFetch = () => {
			FireServices.getPolices(
				page + 1,
				rowsPerPage,
				appliedFilters.text,
				appliedFilters.status,
				appliedFilters.clientId ?? "",
				appliedFilters.dataIni,
				appliedFilters.dataFim
			)
				.then((response) => {
					if (!response || !Array.isArray(response.data)) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: (
								<ToastContent title="Erro" content="Erro ao buscar apólices" />
							),
						});
						return;
					}

					setData(response.data);
					setTotalCount(response.totalCount ?? 0);
				})
				.finally(() => setIsLoading(false));
		};

		debounce(doFetch);
	}, [page, rowsPerPage, appliedFilters, refreshTrigger]);

	// ---------------------------------------------------------
	// Filtro / busca
	// ---------------------------------------------------------
	const handleSearch = () => {
		const raw = textSearch.trim();
		let processed = raw;

		if (/^\d{2,}/.test(raw)) processed = raw.replace(/\D/g, "");

		setAppliedFilters({
			text: processed,
			dataIni,
			dataFim,
			status,
			clientId,
		});

		setPage(0);
	};

	const refreshList = () => setRefreshTrigger((p) => p + 1);

	// ---------------------------------------------------------
	// Abrir detalhes
	// ---------------------------------------------------------
	// ---------------------------------------------------------
	// Abrir detalhes
	// ---------------------------------------------------------
	const handleOpenPolice = async (id: string, tab?: string) => {
		setIsLoading(true);
		try {
			const resp = await FireServices.getPoliceByID(id);

			if (resp instanceof Error) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: (
						<ToastContent title="Erro" content="Não foi possível carregar os detalhes da apólice." />
					),
				});
				return;
			}

			const finalTab: PoliceTab = (tab as PoliceTab) ?? "dados";

			setModalState({
				open: true,
				row: resp,
				initialTab: finalTab,
			});
		} catch (error) {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: (
					<ToastContent title="Erro" content="Erro ao buscar detalhes da apólice." />
				),
			});
		} finally {
			setIsLoading(false);
		}
	};


	// ---------------------------------------------------------
	// Menu
	// ---------------------------------------------------------
	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: PoliceEvent) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuRow(row);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
		setSelectedMenuRow(null);
	};

	// ---------------------------------------------------------
	// Cancelamento
	// ---------------------------------------------------------
	const handleOpenCancelModal = (id: string) => {
		const row = data.find((r) => r.id === id);
		if (row) {
			setCancelRow(row);
			setCancelModalOpen(true);
		}
		// Fecha o menu se estiver aberto (já é feito no policeMenuActions, mas garante)
		handleCloseMenu();
	};

	const handleConfirmCancel = async (id: string, reason: string) => {
		try {
			await FireServices.firePolicesCancel(id, reason);
			toastHandler.present({
				type: "success",
				messageOrContent: <ToastContent title="Sucesso" content="Apólice cancelada com sucesso!" />,
			});
			setCancelModalOpen(false);
			setCancelRow(null);
			refreshList();
		} catch (error: any) {
			toastHandler.present({
				type: "error",
				messageOrContent: <ToastContent title="Erro" content={error.message || "Erro ao cancelar apólice."} />,
			});
		}
	};

	// ---------------------------------------------------------
	// Utilitário PDF
	// ---------------------------------------------------------
	const openBase64Pdf = (base64: string) => {
		const byteCharacters = atob(base64);
		const byteArray = Uint8Array.from(byteCharacters, (c) => c.charCodeAt(0));
		const file = new Blob([byteArray], { type: "application/pdf" });
		const fileUrl = URL.createObjectURL(file);

		setPdfUrl(fileUrl);
		setPdfModalOpen(true);
	};

	// ---------------------------------------------------------
	// Ações do menu (refatorado!)
	// ---------------------------------------------------------
	const handleMenuAction = (action: string, id: string) => {
		policeMenuActions(action, id, {
			toastHandler,
			openPolice: handleOpenPolice,
			openPdf: openBase64Pdf,
			refreshList,
			closeMenu: handleCloseMenu,
			setIsLoading,
			navigate,
			openCancelModal: handleOpenCancelModal,
		});
	};

	const handleMenuSelect = (action: string) => {
		if (selectedMenuRow) handleMenuAction(action, selectedMenuRow.id);
	};

	// ---------------------------------------------------------
	// Nova apólice
	// ---------------------------------------------------------
	const handleOpenNewPolicePage = () => navigate("/apolices/nova");

	// ---------------------------------------------------------
	// Render
	// ---------------------------------------------------------
	return (
		<Box sx={{ pt: 1 }}>
			<PoliceToolbar
				showInputSearch
				showDateRange
				showStatusSelect
				showClientSelect
				textSearch={textSearch}
				onChangeTextSearch={setTextSearch}
				onClearTextSearch={() => setTextSearch("")}
				onChangeDateStart={setDataIni}
				onChangeDateEnd={setDataFim}
				onChangeStatus={setStatus}
				onChangeClient={(id) => {
					setClientId(id);
					setPage(0);
				}}
				onClickRefresh={handleSearch}
				showNewPoliceButton
				onClickNewPolice={handleOpenNewPolicePage}
			/>

			<LoadingComp isopen={isLoading} />

			<TablePolices
				rows={data}
				totalCount={totalCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={setPage}
				onRowsPerPageChange={setRowsPerPage}
				onOpenPolice={handleOpenPolice}
				onOpenMenu={handleOpenMenu}
			/>

			<PoliceMenu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseMenu}
				selectedRow={selectedMenuRow}
				onSelect={handleMenuSelect}
			/>

			{/* Detalhes da Apólice */}
			<ModalFirePoliceDetails
				open={modalState.open}
				onClose={() => setModalState((prev) => ({ ...prev, open: false }))}
				data={modalState.row}
				isLoading={isLoading}
			/>

			{/* 🆕 PDF Preview Modal */}
			<ModalPreviewDocument
				open={pdfModalOpen}
				onClose={() => setPdfModalOpen(false)}
				previewUrl={pdfUrl}
				previewDoc={{ mimetype: "application/pdf" }}
				isLoading={false}
			/>

			{/* 🆕 Cancel Modal */}
			<ModalFirePoliceCancel
				open={cancelModalOpen}
				onClose={() => setCancelModalOpen(false)}
				data={cancelRow}
				onConfirm={handleConfirmCancel}
			/>
		</Box>
	);
};
